import { extract, isNullish, useArrayKeyboardHandler, } from '@vpap_svelte/internal';
export function useGridKeyboard(options) {
    let data = $derived(extract(options.data));
    let rowHandler = useArrayKeyboardHandler({ orientation: options.orientation ?? 'horizontal' });
    let row = $state(undefined);
    let column = $state(undefined);
    let columnHandler = useArrayKeyboardHandler({
        orientation: options.orientation === 'vertical' ? 'horizontal' : 'vertical',
    });
    let rows = $derived(data.length);
    let columns = $derived.by(() => {
        let maxCols = 0;
        for (const row of data) {
            if (row.length > maxCols) {
                maxCols = row.length;
            }
        }
        return maxCols;
    });
    return {
        get options() {
            return options;
        },
        get rows() {
            return rows;
        },
        get columns() {
            return columns;
        },
        reset() {
            row = undefined;
            column = undefined;
        },
        nextRow() {
            if (row === undefined) {
                if (rows === 0)
                    return;
                else {
                    row = 0;
                }
            }
            else {
                if (row < rows - 1) {
                    row++;
                }
                else if (options.wrap) {
                    row = 0;
                }
            }
        },
        previousRow() {
            if (row === undefined) {
                if (rows === 0)
                    return;
                if (options.wrap) {
                    row = rows - 1;
                }
            }
            else if (row !== 0) {
                row--;
            }
            else if (options.wrap) {
                row = rows - 1;
            }
        },
        /**
         * Advances the cursor to the next column
         * @returns
         */
        nextColumn() {
            const cursor = column;
            const numCols = columns;
            if (cursor === undefined) {
                if (numCols === 0)
                    return;
                column = 0;
            }
            else {
                // if we're not on the last row
                if (cursor < numCols - 1) {
                    column = cursor + 1;
                }
                else if (this.options.wrap) {
                    column = 0;
                }
            }
        },
        /**
         * Moves the cursor to the previous column
         * @returns
         */
        previousColumn() {
            const cursor = column;
            const numCols = this.columns;
            if (cursor === undefined) {
                if (numCols === 0)
                    return;
                if (this.options.wrap) {
                    column = numCols - 1;
                }
            }
            else {
                //
                if (cursor !== 0) {
                    column = cursor - 1;
                }
                else if (this.options.wrap) {
                    column = numCols - 1;
                }
            }
        },
        /**
         * Returns the current row. If there is no row cursor, returns undefined
         */
        get row() {
            if (row === undefined)
                return undefined;
            return data[row];
        },
        /**
         * Returns the set of records around the current column. If there is no
         * column cursor, returns undefined
         */
        get column() {
            if (column === undefined)
                return undefined;
            let records = [];
            for (let i = 0; i < this.rows; i++) {
                const row = data[i];
                // Typescript doesn't treat this as possibly undefined which ofc is false
                records.push(row[column]);
            }
            return records;
        },
        /**
         * Returns the current selection:
         * - If there is no column cursor or row cursor, returns undefined
         * - If there is only a column cursor, returns that column
         * - If there is only a row cursor, returns that row
         * - Otherwise, returns the cell
         */
        get selection() {
            if (isNullish(column) && isNullish(row))
                return undefined;
            if (isNullish(column)) {
                return data[row];
            }
            if (isNullish(row)) {
                let records = [];
                for (let i = 0; i < rows; i++) {
                    const row = data[i];
                    records.push(row[column]);
                }
                return records;
            }
            return data[row][column];
        },
        get attributes() {
            return {
                role: 'application',
                tabindex: 0,
                onkeydown: (e) => {
                    const arrows = ['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'];
                    if (!arrows.includes(e.code))
                        return;
                    e.preventDefault();
                    if (rowHandler.isNextKey(e)) {
                        this.nextRow();
                    }
                    else if (rowHandler.isPreviousKey(e)) {
                        this.previousRow();
                    }
                    else if (columnHandler.isNextKey(e)) {
                        this.nextColumn();
                    }
                    else if (columnHandler.isPreviousKey(e)) {
                        this.previousColumn();
                    }
                },
                onblur: () => {
                    this.reset();
                },
            };
        },
    };
}
